import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "../ui/Dialog";
import Button from "../ui/Button";
import TermsView from "./TermsView";
import TermsAcceptBar from "./TermsAcceptBar";
import { useTermsReader } from "./useTermsReader";
import { termsKindLabel, type PlatformTermsKind } from "../../api/General/PlatformTerms";

interface TermsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: PlatformTermsKind;
  /** When true, an "I accept" button posts the acceptance before closing. */
  autoAccept?: boolean;
  /** Called after acceptance (or "Close" if autoAccept=false). */
  onAccepted?: () => void;
}

const TermsDialog: React.FC<TermsDialogProps> = ({
  open,
  onOpenChange,
  kind,
  autoAccept = false,
  onAccepted,
}) => {
  const { t } = useTranslation();

  const reader = useTermsReader(kind, {
    autoAccept,
    enabled: open,
    onAccepted: () => {
      onAccepted?.();
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="overflow-hidden flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{t(termsKindLabel(kind))}</DialogTitle>
          <DialogDescription>
            {t("Please review the document before continuing.")}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col flex-1 min-h-0 overflow-hidden p-0">
          <TermsView reader={reader} kind={kind} embedded hideHeader />
        </DialogBody>
        <DialogFooter className="flex-wrap items-center justify-end gap-x-3 gap-y-2">
          {autoAccept ? (
            <TermsAcceptBar reader={reader} />
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("Close")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TermsDialog;
